<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Checkbox, Input } from '@components'
import { getPrice } from '@utils'

import { useStaffItem } from '@store/staffItem'
const $staffItem = useStaffItem()

import { useService } from '@store/service'
const $service = useService()

import { useSettings } from '@store/settings'
const $settings = useSettings()

const sortCategory = computed(() => {
    return $service.getCategories.sort((a, b) => a.meta.priority - b.meta.priority)
})

const getCategories = computed(() => {
    const cats = sortCategory.value
    const services = $service.getServices

    if (!cats || !services) return []

    const _cats = cats.map(cat => {
        let _services = services.filter(service => service.category_id == cat.category_id)
        _services = _services.sort((a, b) => a.meta.order - b.meta.order).filter(service => service.status === 'publish')
        return {
            category_id: cat.category_id,
            name: cat.name,
            services: _services.map(service => {
                return {
                    service_id: service.service_id,
                    name: service.name,
                    price: service.meta.price,
                }
            }),
        }
    })

    return _cats.filter(cat => cat.services.length)
})

const isCatSelected = (category_id) => {
    // Return true if all services of this category is selected, return -1 if some services are selected, return false if no service is selected
    const services = getCategories.value.find(cat => cat.category_id === category_id)?.services
    if (!services) return false

    if (!$staffItem.staff.services.length) return false

    const selectedServices = services.filter(service => $staffItem.staff.services.includes(service.service_id))
    if (selectedServices.length === services.length) return true

    if (selectedServices.length > 0) return -1

    return false
}

const isServiceSelected = (service_id) => {
    if (!$staffItem.staff.services.length) return false
    return $staffItem.staff.services.includes(service_id)
}

const selectCat = (category_id) => {
    const services = getCategories.value.find(cat => cat.category_id === category_id)?.services
    if (!services) return

    const selectedServices = services.filter(service => $staffItem.staff.services.includes(service.service_id))
    if (selectedServices.length === services.length) {
        // Unselect all services
        services.forEach(service => {
            service.meta = {
                checkbox: false,
            }
            $staffItem.staff.services = $staffItem.staff.services.filter(id => id !== service.service_id)
        })
    } else {
        // Select all services
        services.forEach(service => {
            service.meta = {
                checkbox: true,
            }
            $staffItem.staff.services.push(service.service_id)
        })
    }
}

const selectService = (service_id) => {
    // If service is already selected, remove it from staff services array
    if ($staffItem.staff.services.includes(service_id)) {
        $staffItem.staff.services = $staffItem.staff.services.filter(id => id !== service_id)
        return
    }

    // Add service id in staff services array
    $staffItem.staff.services.push(service_id)
}

const isCustomPrice = ref(false)

</script>
<template>
    <div>
        <div v-if="$staffItem.state.loading" class="flex flex-col gap-5 motion-safe:animate-pulse">
            <div class="bg-[#F2F4F7] rounded-md h-[28px] w-[100px]"></div>
            <div class="border border-[#F2F4F7] rounded-lg p-4">
                <div v-for="i in 3">
                    <div class="bg-[#F2F4F7] rounded-md h-[28px] w-full"></div>
                    <div v-if="i !== 3" class="bg-[#F2F4F7] h-[1px] w-full my-3"></div>
                </div>
            </div>
        </div>
        <div v-else>
            <div v-for=" (category, index) in getCategories" :key="index" class="mb-6">
                <div class="flex items-center gap-3 mb-[18px] ml-4">
                    <Checkbox :checked="isCatSelected(category.category_id)" @click="selectCat(category.category_id)">
                        <div class="text-[#344054] text-lg font-semibold">{{ category.name }}</div>
                    </Checkbox>

                </div>
                <div class="border bg-white p-4 rounded-lg border-solid border-[#D0D5DD]">
                    <div v-for="(service, index) in category.services" :key="index" :class="{ 'opacity-60  line-through': !isServiceSelected(service.service_id) }" class="h-[41px] flex gap-3 justify-between items-center text-base font-medium leading-6 pt-3 pb-3 first:pt-0 last:pb-0 border-b border-[#D0D5DD] last:border-b-0">
                        <div class="flex items-center gap-3 ml-1">
                            <Checkbox :checked="isServiceSelected(service.service_id)" @click="selectService(service.service_id)">{{ service.name }}</Checkbox>
                        </div>
                        <div v-if="!isCustomPrice" class="text-[#101828] pr-3">{{ getPrice(service.price) }}</div>
                        <Input v-else :iconLeft="$settings.payment.currency_symbol" v-model="service.price" @change="updateServicePrice(service)" class="w-[110px]" />
                    </div>
                </div>
            </div>
            <div v-if="getCategories.length == 0" class="text-[red]">No {{ String($settings.labels.service?.singular).toLowerCase() }} found</div>
        </div>
    </div>
</template>


<style>

</style>